"use client";

import { useEffect, useState } from "react";
import { EnquiryFormTabs, type EnquiryFormTab } from "@/components/forms/EnquiryFormTabs";
import type { PublicEnquiryType } from "@/lib/enquiry";
import { Eyebrow } from "@/components/ui/Badge";
import { cn } from "@/lib/utils";

type Mode = "general" | "enquiry";

const FORM_TABS: EnquiryFormTab[] = ["general", "travel", "personality", "it", "corporate", "ielts"];

const MODES: { id: Mode; label: string }[] = [
  { id: "general", label: "General" },
  { id: "enquiry", label: "Enquiry" },
];

export function ContactForms({
  defaultMode = "general",
  defaultForm,
  defaultCourse,
  autoScroll = false,
  types,
}: {
  defaultMode?: Mode;
  defaultForm?: string;
  defaultCourse?: { title: string; category?: string };
  /** Bring the form into view on load (arriving from an "Enquiry Now" button). */
  autoScroll?: boolean;
  types?: PublicEnquiryType[];
}) {
  const [mode, setMode] = useState<Mode>(defaultMode);

  useEffect(() => {
    if (!autoScroll) return;
    // wait a beat so the reveal animation and fonts have settled before measuring
    const t = window.setTimeout(() => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    return () => window.clearTimeout(t);
  }, [autoScroll]);
  const defaultFormTab: EnquiryFormTab = FORM_TABS.includes(defaultForm as EnquiryFormTab)
    ? (defaultForm as EnquiryFormTab)
    : "general";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Eyebrow>Write to us</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-semibold text-ink">Send us a message</h2>
        </div>
        <div
          role="tablist"
          aria-label="Form type"
          className="inline-flex gap-1 rounded-[var(--radius-btn)] border border-line bg-cream/70 p-1.5"
        >
          {MODES.map((m) => {
            const on = mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setMode(m.id)}
                className={cn(
                  "h-10 rounded-xl px-5 text-sm font-semibold transition-all",
                  on
                    ? "bg-brand-600 text-white shadow-soft"
                    : "text-ink-soft hover:bg-white hover:text-brand-700"
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        {mode === "general" ? (
          <EnquiryFormTabs key="general" scope="general" types={types} />
        ) : (
          <EnquiryFormTabs key="enquiry" defaultTab={defaultFormTab} defaultCourse={defaultCourse} types={types} />
        )}
      </div>
    </div>
  );
}
